import { Icon } from "@/components/Icon/Icon";
import { cn } from "@/utils/cn";
import type { FamilyKey } from "@/utils/types/flavor";
import type { IconName } from "@/utils/types/ui";
import { CatalogMode } from "./CatalogMode";
import { OwnLibraryMode } from "./OwnLibraryMode";
import { DescribeMode } from "./DescribeMode";
import type { Anchor, AnchorMode } from "./anchorTypes";

export type { Anchor, AnchorMode, LibMode } from "./anchorTypes";

const ANCHOR_MODES: { id: AnchorMode; label: string; icon: IconName }[] = [
  { id: "catalog", label: "Pick from our catalog", icon: "bookmark" },
  { id: "library", label: "Pick from your own library", icon: "box" },
  { id: "describe", label: "Describe the target", icon: "text" },
];

type AnchorPanelProps = {
  draftId: string;
  value: Anchor;
  onChange: (next: Anchor) => void;
  baseFamily: FamilyKey | null;
  reRankPulse: boolean;
  onDuplicateNameChange?: (isDuplicate: boolean) => void;
  onOpenLibrary: () => void;
};

export function AnchorPanel({
  draftId,
  value,
  onChange,
  baseFamily,
  reRankPulse,
  onDuplicateNameChange,
  onOpenLibrary,
}: AnchorPanelProps) {
  const set = (patch: Partial<Anchor>) => onChange({ ...value, ...patch });

  return (
    <div className="panel" style={{ padding: 20, marginBottom: 16 }}>
      <div
        className="hstack"
        style={{
          justifyContent: "space-between",
          marginBottom: 14,
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div className="vstack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Anchor your target reference</div>
          <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.4 }}>
            Point the Flavor AI at what you're aiming for — a reference we already have, something from
            your own library, or a description in your own words.
          </div>
        </div>
      </div>

      <div className="anchor-mode-switch" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        {ANCHOR_MODES.map((t) => {
          const sel = value.mode === t.id;
          return (
            <button
              key={t.id}
              type="button"
              className={cn("anchor-mode-btn", sel && "active")}
              onClick={() => set({ mode: t.id })}
            >
              <Icon name={t.icon} size={12} />
              {t.label}
            </button>
          );
        })}
      </div>

      {value.mode === "catalog" && (
        <CatalogMode
          pickedRec={value.pickedRec}
          onPick={(rec) => set({ pickedRec: rec })}
          baseFamily={baseFamily}
          reRankPulse={reRankPulse}
        />
      )}
      {value.mode === "library" && (
        <OwnLibraryMode
          draftId={draftId}
          value={value}
          set={set}
          onDuplicateNameChange={onDuplicateNameChange}
          onOpenLibrary={onOpenLibrary}
        />
      )}
      {value.mode === "describe" && <DescribeMode />}
    </div>
  );
}
